/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantMatrix = this.getVariantMatrix();
      this.availabilityAnchor = this.getAvailabilityAnchor();
      this.variantRequestId = 0;
      this.suppressVariantChange = false;

      if (this.applyInitialVariantFallback()) return;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundBlockUnavailableSelection = this.blockUnavailableSelection.bind(this);
      this.addEventListener('click', this.boundBlockUnavailableSelection, true);
      this.addEventListener('keydown', this.boundBlockUnavailableSelection, true);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.removeEventListener('click', this.boundBlockUnavailableSelection, true);
      this.removeEventListener('keydown', this.boundBlockUnavailableSelection, true);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    applyInitialVariantFallback() {
      if (this.quickBuyContainer || this.featuredProductContainer || !this.productAvailable) return false;

      if (window.__amazerBathVariantFallbackChecked) return false;
      window.__amazerBathVariantFallbackChecked = true;

      const fallbackVariantId = this.dataset.firstAvailableVariantId;
      if (!fallbackVariantId) return false;

      const url = new URL(window.location.href);
      const requestedVariantId = url.searchParams.get('variant');
      const currentVariantId = this.variant ? String(this.variant.id) : null;

      if (
        requestedVariantId
        && this.variant
        && !this.variant.available
        && requestedVariantId !== fallbackVariantId
      ) {
        url.searchParams.set('variant', fallbackVariantId);
        window.location.replace(url.toString());
        return true;
      }

      if (
        !requestedVariantId
        && this.dataset.defaultVariantAvailable === 'false'
        && currentVariantId === fallbackVariantId
      ) {
        url.searchParams.set('variant', fallbackVariantId);
        window.history.replaceState({}, '', url.toString());
      }

      return false;
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
      this.updateSelectionSummary();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (this.suppressVariantChange) return;

      const changedSelector = evt.target.closest('.option-selector');
      if (changedSelector) {
        this.availabilityAnchor = Array.from(this.optionSelectors).indexOf(changedSelector);
        this.storeAvailabilityAnchor();
      }

      const resolvedVariant = this.resolveUnavailableCombination();
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();
      this.updateAvailability();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = selectedOptionIds.includes(null)
        ? null
        : resolvedVariant?.id || evt.target.dataset.variantId || evt.detail?.variantId;
      this.updateSelectionSummary();
      this.updateAddToCartButton();
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          this.updateAvailability();

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Unavailable values stay interactive. Selecting one preserves that choice
     * and lets resolveUnavailableCombination move the other option(s) to stock.
     */
    blockUnavailableSelection() {}

    /**
     * Keeps the latest clicked value. If the resulting combination is sold out,
     * incompatible selections in the other option groups are cleared so the
     * buyer can make the missing choice explicitly.
     * @returns {?object} Exact available variant, or null for partial selection.
     */
    resolveUnavailableCombination() {
      const selectedOptions = this.getSelectedOptions();
      if (!this.variantMatrix.length) return null;

      if (selectedOptions.includes(null)) {
        const anchorIndex = this.availabilityAnchor;
        const anchorValue = selectedOptions[anchorIndex];
        if (anchorIndex >= 0 && anchorValue !== null) {
          const globallyUnavailable = !this.variantMatrix.some((variant) => (
            variant.available && variant.options[anchorIndex] === anchorValue
          ));
          this.incompatibleSelection = { anchorIndex, globallyUnavailable };
        }
        return null;
      }

      const exactVariant = this.variantMatrix.find((variant) => (
        variant.options.every((value, index) => value === selectedOptions[index])
      ));
      if (exactVariant?.available) {
        this.incompatibleSelection = null;
        return exactVariant;
      }

      const anchorIndex = this.availabilityAnchor;
      if (anchorIndex < 0) return exactVariant || null;
      const anchorValue = selectedOptions[anchorIndex];
      const globallyUnavailable = !this.variantMatrix.some((variant) => (
        variant.available && variant.options[anchorIndex] === anchorValue
      ));

      this.suppressVariantChange = true;
      this.optionSelectors.forEach((selector, index) => {
        if (index !== anchorIndex) this.clearOptionValue(selector);
      });
      this.suppressVariantChange = false;
      this.incompatibleSelection = { anchorIndex, globallyUnavailable };
      this.variant = null;
      return null;
    }

    /**
     * Selects an option value while a parent change handler is suppressed.
     * @param {Element} selector - Option selector.
     * @param {string} value - Value to select.
     */
    selectOptionValue(selector, value) {
      if (selector.dataset.selectorType === 'dropdown') {
        const option = selector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]`);
        option?.closest('custom-select')?.selectOption(option);
      } else {
        const input = Array.from(selector.querySelectorAll('input')).find((candidate) => candidate.value === value);
        if (input) input.checked = true;
      }
    }

    /**
     * Clears a conflicting option without dispatching another variant change.
     * @param {Element} selector - Option selector to clear.
     */
    clearOptionValue(selector) {
      if (selector.dataset.selectorType === 'dropdown') {
        const blankOption = selector.querySelector('.custom-select__option[data-value=""]');
        if (blankOption) {
          blankOption.closest('custom-select')?.selectOption(blankOption);
        } else {
          selector.querySelectorAll('.custom-select__option').forEach((option) => {
            option.setAttribute('aria-selected', 'false');
          });
          const nativeSelect = selector.querySelector('.custom-select__native');
          if (nativeSelect) nativeSelect.value = '';
        }
      } else {
        selector.querySelectorAll('input:checked').forEach((input) => {
          input.checked = false;
        });
      }
    }

    getOptionPlaceholder(optionIndex) {
      const optionName = (this.optionSelectors[optionIndex]?.dataset.option || 'option').trim().toLowerCase();
      return `Select a ${optionName}`;
    }

    updateSelectionSummary() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      const selectedOptions = this.getSelectedOptions();
      productForm.querySelectorAll('.product-info__selected-variant-value').forEach((summary) => {
        summary.replaceChildren();
        selectedOptions.forEach((value, index) => {
          if (index > 0) summary.append(document.createTextNode(' · '));
          const part = document.createElement('span');
          part.textContent = value || this.getOptionPlaceholder(index);
          if (!value) part.className = 'product-info__selected-variant-placeholder';
          summary.append(part);
        });
      });

      const showUnavailableMessage = Boolean(this.incompatibleSelection?.globallyUnavailable);
      productForm.querySelectorAll('.product-info__selection-message').forEach((message) => {
        const anchorIndex = this.incompatibleSelection?.anchorIndex;
        const optionName = (this.optionSelectors[anchorIndex]?.dataset.option || 'option').trim().toLowerCase();
        const otherOptionNames = Array.from(this.optionSelectors)
          .filter((selector, index) => index !== anchorIndex)
          .map((selector) => (selector.dataset.option || 'option').trim().toLowerCase());
        let unavailableScope = 'all combinations';
        if (otherOptionNames.length === 1) {
          const otherName = otherOptionNames[0];
          const pluralName = otherName === 'quantity' ? 'quantities' : `${otherName}s`;
          unavailableScope = `all ${pluralName}`;
        }
        message.textContent = `This ${optionName} is currently unavailable in ${unavailableScope}.`;
        message.hidden = !showUnavailableMessage;
      });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'false' || !this.variantMatrix.length) return;

      const selectedOptions = this.getSelectedOptions();

      this.optionSelectors.forEach((selector, optionIndex) => {
        selector.querySelectorAll('.js-option').forEach((option) => {
          if (option.dataset.productUrl || option.dataset.value === '') return;

          const value = option.dataset.value || option.value;
          const available = this.variantMatrix.some((variant) => (
            variant.available
            && variant.options[optionIndex] === value
            && selectedOptions.every((selectedValue, index) => (
              index === optionIndex || selectedValue === null || variant.options[index] === selectedValue
            ))
          ));

          option.classList.toggle('is-unavailable', !available);
          option.classList.remove('is-disabled');
          option.removeAttribute('aria-disabled');

          if (option.matches('input')) {
            option.disabled = false;
            const label = selector.querySelector(`label[for="${CSS.escape(option.id)}"]`);
            label?.removeAttribute('aria-disabled');
          }
        });
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Partial selection after an incompatible value was clicked
      const selectedOptions = this.getSelectedOptions();
      if (selectedOptions.indexOf(null) !== -1) {
        this.addBtn.disabled = true;
        if (this.incompatibleSelection?.globallyUnavailable) {
          this.addBtn.textContent = theme.strings.noStock;
        } else {
          const missingIndex = selectedOptions.findIndex((value) => value === null);
          this.addBtn.textContent = this.getOptionPlaceholder(missingIndex);
        }
        productForm.querySelectorAll('.js-liner-buy-now').forEach((button) => {
          button.disabled = true;
        });
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        productForm.querySelectorAll('.js-liner-buy-now').forEach((button) => {
          button.disabled = true;
        });
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
      productForm.querySelectorAll('.js-liner-buy-now').forEach((button) => {
        button.disabled = false;
      });
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[data-selected-variant]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets the compact product variant matrix embedded by Liquid.
     * @returns {Array} Variant ids, availability and option values.
     */
    getVariantMatrix() {
      const dataEl = this.querySelector('[data-variant-matrix]');
      return dataEl ? JSON.parse(dataEl.textContent) : [];
    }

    /**
     * Gets the last option explicitly changed for this product.
     * The clicked value is preserved when an unavailable combination needs fallback.
     * @returns {number} Zero-based option index, or -1.
     */
    getEffectiveAvailabilityAnchor() {
      return this.availabilityAnchor;
    }

    getAvailabilityAnchor() {
      window.__amazerBathAvailabilityAnchors = window.__amazerBathAvailabilityAnchors || {};
      const stored = window.__amazerBathAvailabilityAnchors[this.dataset.url];
      return Number.isInteger(stored) ? stored : -1;
    }

    /**
     * Stores the latest explicit option choice across section HTML refreshes.
     */
    storeAvailabilityAnchor() {
      window.__amazerBathAvailabilityAnchors = window.__amazerBathAvailabilityAnchors || {};
      window.__amazerBathAvailabilityAnchors[this.dataset.url] = this.availabilityAnchor;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

